export interface PlaceSearchCountry {
  code: string;
  name: string;
}

const COUNTRY_ALIASES: { code: string; name: string; patterns: string[] }[] = [
  { code: 'US', name: 'United States', patterns: ['united states', 'usa', 'u.s.a.', 'u.s.', 'america'] },
  { code: 'GB', name: 'United Kingdom', patterns: ['united kingdom', 'great britain', 'britain', 'uk'] },
];

function normalizeCode(code: string | null | undefined): string {
  return (code || '').trim().toUpperCase();
}

/** True when `name` appears as its own phrase in the query. */
function queryMentionsPhrase(query: string, phrase: string, flags = 'i'): boolean {
  const trimmed = phrase.trim();
  if (trimmed.length < 2) {
    return false;
  }
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
  // Letters of either case are word characters, so a capital is not a separator.
  return new RegExp(`(^|[^A-Za-z0-9])${escaped}([^A-Za-z0-9]|$)`, flags).test(query);
}

function queryMentionsPattern(query: string, pattern: string): boolean {
  // A 2-letter ISO code matches only as its own word, in either case.
  // "AR" can mean Argentina; the same letters inside "STARBUCKS" do not.
  if (/^[A-Z]{2}$/.test(pattern)) {
    return queryMentionsPhrase(query, pattern, 'i');
  }
  return queryMentionsPhrase(query, pattern);
}

/** Countries named in a Place query, using live tenancy names plus common aliases. */
export function mentionedCountriesInQuery(
  query: string,
  countries: PlaceSearchCountry[]
): PlaceSearchCountry[] {
  const found = new Map<string, PlaceSearchCountry>();
  const candidates: { code: string; name: string; patterns: string[] }[] = [
    ...countries.map((country) => ({
      code: normalizeCode(country.code),
      name: country.name,
      patterns: [country.name, normalizeCode(country.code)],
    })),
    ...COUNTRY_ALIASES,
  ].filter((item) => item.code);

  candidates.sort((a, b) => b.name.length - a.name.length);

  for (const candidate of candidates) {
    if (found.has(candidate.code)) {
      continue;
    }
    if (candidate.patterns.some((pattern) => queryMentionsPattern(query, pattern))) {
      found.set(candidate.code, { code: candidate.code, name: candidate.name });
    }
  }

  return [...found.values()];
}

/**
 * Copy when the query names a country the searcher is not in
 * (e.g. "hospital, harper, Liberia" from the United States).
 * Named places are looked up across the searcher's own country.
 */
export function buildPlaceSearchAwayNotice(
  query: string,
  userCountry: PlaceSearchCountry | null,
  countries: PlaceSearchCountry[]
): string | null {
  const mentioned = mentionedCountriesInQuery(query, countries);
  if (mentioned.length === 0) {
    return null;
  }

  const userCode = normalizeCode(userCountry?.code);
  const awayFromUser = mentioned.filter((country) => country.code !== userCode);
  if (awayFromUser.length === 0) {
    return null;
  }

  const named = awayFromUser.map((country) => country.name).join(' or ');
  if (userCountry?.name) {
    return `Place search stays in the country you are in. You appear to be in ${userCountry.name}, not ${named}. It will not find a place in ${named}.`;
  }

  return `Place search stays in your current country. This query names ${named}. It will not search there unless you are in ${named}.`;
}
